import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Eye, Search } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/components/ui/Toast";
import { Modal } from "@/components/ui/Modal";
import { Badge } from "@/components/ui/Badge";
import { Spinner } from "@/components/ui/Spinner";
import type { User } from "@/types";

function initials(name: string) {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

export function ImpersonateUserModal({ onClose }: { onClose: () => void }) {
  const [search, setSearch] = useState("");
  const [pendingId, setPendingId] = useState<string | null>(null);
  const { impersonate } = useAuth();
  const { notify } = useToast();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["users", "impersonate-search", search],
    queryFn: () => api.getPaginated<User>(`/api/v1.0/users?page=1&page_size=10&search=${encodeURIComponent(search)}`),
  });

  const eligible = (data?.data ?? []).filter((u) => u.role !== "admin" && u.status === "active");

  const onPick = async (u: User) => {
    setPendingId(u.id);
    try {
      await impersonate(u.id);
      notify(`Now viewing as ${u.full_name}`);
      onClose();
      navigate("/");
    } catch (err) {
      notify(err instanceof ApiError ? err.message : "Failed to impersonate user", "error");
      setPendingId(null);
    }
  };

  return (
    <Modal title="View as user" onClose={onClose}>
      <div className="flex flex-col gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <input
            autoFocus
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
        </div>

        <div className="flex max-h-80 flex-col divide-y divide-slate-100 overflow-y-auto">
          {isLoading ? (
            <p className="py-8 text-center text-sm text-slate-400">Loading...</p>
          ) : eligible.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-400">No active students or instructors found.</p>
          ) : (
            eligible.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => onPick(u)}
                disabled={pendingId !== null}
                className="flex items-center justify-between gap-3 py-2.5 text-left transition-colors hover:bg-slate-50 disabled:opacity-50"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-700">
                    {initials(u.full_name)}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-800">{u.full_name}</p>
                    <p className="truncate text-xs text-slate-500">{u.email}</p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Badge tone={u.role === "instructor" ? "blue" : "slate"}>{u.role}</Badge>
                  {pendingId === u.id ? <Spinner className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5 text-slate-400" />}
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}
